import React, { useState } from "react";
import { SubNav, FileDropzone, ImageCropper } from "../gc/ui";
import SpriteEditorPanel from "./SpriteEditorPanel";
import AnimationPanel from "./AnimationPanel";
import SpriteSearchPanel from "./SpriteSearchPanel";
import HudDesignerPanel from "./HudDesignerPanel";
import { projects as projectsApi } from "../../lib/api";
import {
  SPRITE_STUDIOS, readAsDataUrl, downloadDataUrl, recolorDataUrl, stitchAtlas,
  probeImageSize, nearestCanvasSize,
} from "../../lib/gamecraft";
import { toast } from "sonner";
import ModuleShell, { EmptyCraft, RelatedStrip } from "../gc/ModuleShell";
import { studioHealth, studioCast, matchesFocus } from "../../lib/studioHealth";

export default function SpriteHub({ project, onChange, onJump, focus }) {
  const [studioId, setStudioId] = useState("creatures");
  const [sub, setSub] = useState("editor");
  const [typeFilter, setTypeFilter] = useState("all");
  const [loopOnly, setLoopOnly] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [animImport, setAnimImport] = useState(null);
  const [loadSprite, setLoadSprite] = useState(null);
  const [preview, setPreview] = useState(null);
  const studio = SPRITE_STUDIOS.find((s) => s.id === studioId) || SPRITE_STUDIOS[0];
  const sprites = (project.sprites || []).filter((s) => (s.studio || "creatures") === studio.id);

  const openInEditor = (s, { asCopy = false, grid } = {}) => {
    setLoadSprite({
      id: s.id,
      name: s.name,
      data_url: s.data_url,
      width: grid || s.width || s.height || studio.size || 32,
      category: s.category,
      subtype: s.subtype,
      asCopy,
      nonce: Date.now(),
    });
    setSub("editor");
  };

  const sendToPlaytest = (s) => {
    const role = s.studio === "world" || /tile|piso|floor/i.test(s.name || s.category || "")
      ? "floorId"
      : s.studio === "gear" || /arma|sword|weapon/i.test(s.category || s.name || "")
        ? "weaponId"
        : "creatureId";
    onChange({
      config: {
        ...(project.config || {}),
        playtest: { ...(project.config?.playtest || {}), [role]: s.id },
      },
    });
    onJump?.("engine");
    toast.success(`En el motor como ${role === "floorId" ? "piso" : role === "weaponId" ? "arma" : "criatura"}`);
  };

  const upload = async (files) => {
    const added = [];
    for (const f of files) {
      const data_url = await readAsDataUrl(f);
      let width = studio.size || 32;
      try {
        const sz = await probeImageSize(data_url);
        width = nearestCanvasSize(sz.width, sz.height);
      } catch { /* keep studio size */ }
      const payload = {
        name: f.name.replace(/\.[^.]+$/, ""),
        data_url,
        category: studio.kinds[0].id,
        subtype: (studio.subtypes || studio.kinds)[0]?.id,
        studio: studio.id,
        width,
        height: width,
        source: "upload",
        tags: ["upload"],
      };
      try {
        added.push(await projectsApi.addSprite(project.project_id, payload));
      } catch {
        const local = { id: crypto.randomUUID(), ...payload };
        added.push(local);
        onChange({ sprites: [...(project.sprites || []), local] });
      }
    }
    const p = await projectsApi.get(project.project_id).catch(() => null);
    if (p) onChange({ sprites: p.sprites });
    toast.success(added.length > 1
      ? `${added.length} imágenes en la bandeja`
      : "Imagen en la bandeja — mírala, recorta o úsala de base");
    setSub("editor");
    const last = added[added.length - 1];
    if (last) {
      openInEditor(last);
      setPreview(last);
    }
  };

  const score = studioHealth(project).modules.find((m) => m.id === "sprites")?.score ?? 0;
  const cast = studioCast(project);

  return (
    <ModuleShell
      dense
      title="Sprites"
      subtitle="32px se lee en el motor. Silueta primero, detalle después."
      beginner="Dibuja o sube un héroe y un slime. Enlázalos en Ideas y pulsa Al motor."
      expert="Atlas, recolor y recorte. El playtest usa creatureId / weaponId / floorId."
      score={score}
      nextLabel="Probar en motor"
      onNext={() => onJump?.("engine")}
      related={(
        <RelatedStrip items={[
          ...cast.members.filter((m) => m.spr).map((m) => ({
            id: m.spr.id,
            label: m.role,
            img: m.spr.data_url,
            onClick: () => { setStudioId(m.spr.studio || "creatures"); setSub("library"); setLoopOnly(true); },
          })),
          { id: "loop", label: loopOnly ? "Ver todos" : "Sólo el loop", onClick: () => setLoopOnly((v) => !v) },
        ]} />
      )}
    >
    <div className="h-full flex flex-col min-h-0">
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {SPRITE_STUDIOS.map((s) => (
            <button
              key={s.id}
              onClick={() => { setStudioId(s.id); setSub("editor"); setTypeFilter("all"); }}
              className="px-3 py-2 rounded-2xl text-left border min-w-[160px]"
              style={{
                borderColor: studioId === s.id ? "var(--gc-primary)" : "var(--gc-border)",
                background: studioId === s.id ? "var(--gc-primary-soft)" : "var(--gc-surface)",
              }}
            >
              <div className="text-sm font-semibold">{s.label}</div>
              <div className="text-[11px] mt-0.5" style={{ color: "var(--gc-muted)" }}>{s.hint}</div>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SubNav
            items={[
              { id: "editor", label: "Editor" },
              { id: "search", label: "Buscar refs" },
              { id: "anim", label: "Animación" },
              { id: "library", label: "Biblioteca" },
            ]}
            value={sub}
            onChange={setSub}
          />
          <FileDropzone accept="image/*" multiple onFiles={upload} label="Subir imagen" />
          <button
            className="gc-chip"
            onClick={async () => {
              const url = await stitchAtlas(project.sprites || []);
              downloadDataUrl(url, `${project.slug || "game"}-atlas.png`);
              toast.success("Atlas exportado");
            }}
          >
            Export atlas
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        {studio.id === "hud" && sub === "editor" && (
          <div className="gc-card m-4">
            <HudDesignerPanel project={project} onChange={onChange} />
          </div>
        )}
        {studio.id !== "hud" && sub === "editor" && (
          <div className="m-4 flex flex-col lg:flex-row gap-3 items-start">
            <div className="gc-tool flex-1 min-w-0 w-full">
              <SpriteEditorPanel
                project={project}
                onChange={onChange}
                studio={studio}
                loadSprite={loadSprite}
                onLoaded={() => {}}
              />
            </div>
            <SpriteTray
              sprites={sprites}
              activeId={loadSprite?.asCopy ? null : loadSprite?.id}
              onView={(s) => setPreview(s)}
              onEdit={(s) => openInEditor(s)}
              onCopy={(s) => openInEditor(s, { asCopy: true })}
              onBase={(s, size) => openInEditor(s, { asCopy: true, grid: size || studio.size || 32 })}
              onCrop={(s) => setCropSrc({ src: s.data_url, name: s.name })}
              onPlay={sendToPlaytest}
              onDownload={(s) => downloadDataUrl(s.data_url, `${s.name}.png`)}
              onUpload={upload}
            />
          </div>
        )}
        {sub === "search" && (
          <SpriteSearchPanel
            project={project}
            onChange={onChange}
            studio={studio}
            onOpenAnim={(anim) => { setAnimImport(anim); setSub("anim"); }}
          />
        )}
        {sub === "anim" && (
          <div className="gc-tool m-4">
            <AnimationPanel project={project} onChange={onChange} importAnim={animImport} />
          </div>
        )}
        {sub === "library" && (
          <div className="p-4 space-y-3">
            <div className="flex flex-wrap gap-1">
              <button className={"gc-chip " + (typeFilter === "all" ? "gc-chip-on" : "")} onClick={() => setTypeFilter("all")}>Todos</button>
              {(studio.subtypes || studio.kinds || []).map((k) => (
                <button key={k.id} className={"gc-chip " + (typeFilter === k.id ? "gc-chip-on" : "")} onClick={() => setTypeFilter(k.id)}>
                  {k.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
            {sprites.filter((s) => {
              const typeOk = typeFilter === "all" || s.subtype === typeFilter || s.category === typeFilter;
              const loopOk = !loopOnly || cast.loopIds.has(s.id);
              return typeOk && loopOk;
            }).map((s) => (
              <div key={s.id} className={"gc-card p-2 " + (cast.loopIds.has(s.id) ? "gc-loop-on " : "") + (matchesFocus(focus, { spriteId: s.id }) ? "gc-focus-on" : "")}>
                <button type="button" className="aspect-square rounded-xl overflow-hidden flex items-center justify-center w-full" style={{ background: "#0f172a" }} onClick={() => setPreview(s)}>
                  <img src={s.data_url} alt={s.name} className="max-w-full max-h-full" style={{ imageRendering: "pixelated" }} />
                </button>
                <div className="text-xs mt-2 truncate font-medium">{s.name}</div>
                <div className="text-[10px]" style={{ color: "var(--gc-muted)" }}>
                  {s.subtype || s.category}{s.role ? ` · ${s.role}` : ""}{(s.tags || []).length ? ` · ${(s.tags || []).slice(0, 2).join(", ")}` : ""}
                </div>
                <div className="flex gap-1 mt-1 flex-wrap">
                  <button className="gc-chip" onClick={() => setPreview(s)}>Ver</button>
                  <button className="gc-chip" onClick={() => openInEditor(s)}>Editar</button>
                  <button className="gc-chip" onClick={() => openInEditor(s, { asCopy: true })}>Copiar</button>
                  <button className="gc-chip" onClick={() => downloadDataUrl(s.data_url, `${s.name}.png`)}>Descargar</button>
                  <button className="gc-chip" onClick={() => sendToPlaytest(s)}>Al motor</button>
                  <button className="gc-chip" onClick={async () => {
                    const url = await recolorDataUrl(s.data_url, 50);
                    await projectsApi.addSprite(project.project_id, { name: `${s.name}_var`, data_url: url, category: s.category, studio: s.studio }).catch(() => {});
                    const p = await projectsApi.get(project.project_id).catch(() => null);
                    if (p) onChange({ sprites: p.sprites });
                    toast.success("Variante recolor");
                  }}>Recolor</button>
                  <button className="gc-chip" onClick={() => setCropSrc({ src: s.data_url, name: s.name })}>Recortar</button>
                </div>
              </div>
            ))}
            {sprites.filter((s) => {
              const typeOk = typeFilter === "all" || s.subtype === typeFilter || s.category === typeFilter;
              const loopOk = !loopOnly || cast.loopIds.has(s.id);
              return typeOk && loopOk;
            }).length === 0 && (
              <div className="col-span-full">
              <EmptyCraft
                title={`Nada en ${studio.label}`}
                body="Dibuja, busca refs CC, genera con IA o sube una imagen. 32px basta."
                tips={["Al motor asigna criatura, arma o piso", "Recolor crea variantes rápidas", "Export atlas para el empaquetado"]}
              />
              </div>
            )}
            </div>
          </div>
        )}
      </div>
      {cropSrc && (
        <ImageCropper
          src={cropSrc.src || cropSrc}
          onClose={() => setCropSrc(null)}
          onCrop={async (out) => {
            const payload = {
              name: `${cropSrc.name || "recorte"}-crop`,
              data_url: out,
              category: studio.kinds[0].id,
              studio: studio.id,
            };
            let saved = null;
            try {
              saved = await projectsApi.addSprite(project.project_id, payload);
            } catch {
              saved = { id: crypto.randomUUID(), ...payload };
              onChange({ sprites: [...(project.sprites || []), saved] });
            }
            const p = await projectsApi.get(project.project_id).catch(() => null);
            if (p) onChange({ sprites: p.sprites });
            setCropSrc(null);
            openInEditor(saved || payload);
            toast.success("Recorte en el lienzo y en la bandeja");
          }}
        />
      )}
      {preview && (
        <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="gc-card max-w-lg w-full p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold truncate pr-3">{preview.name}</div>
              <button type="button" className="gc-chip" onClick={() => setPreview(null)}>Cerrar</button>
            </div>
            <div className="rounded-xl overflow-hidden flex items-center justify-center mb-3 min-h-[180px]" style={{ background: "#0f172a" }}>
              <img src={preview.data_url} alt={preview.name} className="max-h-[50vh] max-w-full" style={{ imageRendering: "pixelated" }} />
            </div>
            <p className="text-[11px] mb-3" style={{ color: "var(--gc-muted)" }}>
              Úsala de base, recorta una parte o ábrela en el editor.
            </p>
            <div className="flex flex-wrap gap-1">
              <button type="button" className="gc-chip" onClick={() => { openInEditor(preview); setPreview(null); }}>Editar</button>
              <button type="button" className="gc-chip" onClick={() => { openInEditor(preview, { asCopy: true }); setPreview(null); }}>Copiar</button>
              <button type="button" className="gc-chip" onClick={() => { openInEditor(preview, { asCopy: true, grid: 16 }); setPreview(null); }}>Base 16</button>
              <button type="button" className="gc-chip" onClick={() => { openInEditor(preview, { asCopy: true, grid: 32 }); setPreview(null); }}>Base 32</button>
              <button type="button" className="gc-chip" onClick={() => { openInEditor(preview, { asCopy: true, grid: 64 }); setPreview(null); }}>Base 64</button>
              <button type="button" className="gc-chip" onClick={() => { setCropSrc({ src: preview.data_url, name: preview.name }); setPreview(null); }}>Recortar</button>
              <button type="button" className="gc-chip" onClick={() => downloadDataUrl(preview.data_url, `${preview.name}.png`)}>PNG</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </ModuleShell>
  );
}

function SpriteTray({ sprites, activeId, onView, onEdit, onCopy, onBase, onCrop, onPlay, onDownload, onUpload }) {
  return (
    <aside
      className="w-full lg:w-60 shrink-0 gc-card p-3 max-h-[70vh] overflow-auto"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const files = [...(e.dataTransfer.files || [])].filter((f) => f.type.startsWith("image/"));
        if (files.length) onUpload?.(files);
      }}
    >
      <div className="text-[11px] font-semibold mb-2" style={{ color: "var(--gc-muted)" }}>Archivos del estudio</div>
      {sprites.length === 0 && (
        <p className="text-[11px]" style={{ color: "var(--gc-faint)" }}>
          Sube o arrastra una imagen aquí. Se verá, se puede recortar, copiar o usar de base en el lienzo.
        </p>
      )}
      <div className="space-y-2">
        {sprites.map((s) => (
          <div
            key={s.id}
            className="rounded-xl p-2"
            style={{
              background: "var(--gc-surface-soft)",
              border: `1px solid ${activeId === s.id ? "var(--gc-primary)" : "var(--gc-border)"}`,
            }}
          >
            <button
              type="button"
              className="h-20 w-full rounded-lg overflow-hidden flex items-center justify-center mb-1"
              style={{ background: "#0f172a" }}
              onClick={() => onView(s)}
              title="Ver original"
            >
              <img src={s.data_url} alt="" className="max-h-full max-w-full" style={{ imageRendering: "pixelated" }} />
            </button>
            <div className="text-[11px] truncate font-medium">{s.name}</div>
            <div className="flex flex-wrap gap-1 mt-1">
              <button type="button" className="gc-chip" onClick={() => onView(s)}>Ver</button>
              <button type="button" className="gc-chip" onClick={() => onEdit(s)}>Editar</button>
              <button type="button" className="gc-chip" onClick={() => onCopy(s)}>Copiar</button>
              <button type="button" className="gc-chip" onClick={() => onBase(s, 32)}>Base 32</button>
              <button type="button" className="gc-chip" onClick={() => onCrop(s)}>Recortar</button>
              <button type="button" className="gc-chip" onClick={() => onPlay(s)}>Motor</button>
              <button type="button" className="gc-chip" onClick={() => onDownload(s)}>PNG</button>
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
}
